import { CampusDestinationFacility, FacilityLock } from 'api/campus';
import { isDockUnavailableForMovement } from './dock';

/** Move-modal destination rules (locks, availability, order), each destination tagged with its campus facility. */

export interface DestinationFacility {
  facilityId: string;
  facilityName: string;
  facilityAlias?: string | null;
  configuration?: any;
  isAnchor: boolean;
  /** The user may move trailers into this facility's docks. */
  canDock: boolean;
}

export interface DestinationDockDoor {
  dockUuid: string;
  dockName: string;
  zoneUuid: string;
  zoneName: string;
  /** The trailer at the door now, if any. */
  trailer_uuid?: string | null;
  /** Unavailable: the door is locked, or holds a trailer nobody is moving. */
  isLocked: boolean;
  /** Available, but the trailer in it has a move-out task. */
  hasMoveFromTask: boolean;
  configuration?: any;
  facilityId: string;
  facilityName: string;
}

export interface DestinationYardSpot {
  yard_spot_uuid: string;
  yard_spot_name: string;
  facility_zone_uuid: string;
  zoneName: string;
  /** The trailer in the spot now, if any. */
  trailer_uuid?: string | null;
  isOccupied: boolean;
  isLocked: boolean;
  configuration?: any;
  facilityId: string;
  facilityName: string;
}

export interface DestinationFacilityZone {
  facility_zone_uuid: string;
  facility_zone_name: string;
  zone_type?: string;
  configuration?: any;
  trailers: any[];
  facilityId: string;
  facilityName: string;
}

export interface CampusDestinations {
  facilities: DestinationFacility[];
  dockDoors: DestinationDockDoor[];
  yardSpots: DestinationYardSpot[];
  facilityZones: DestinationFacilityZone[];
}

export interface LockSets {
  dockIds: Set<string>;
  yardSpotIds: Set<string>;
  trailerIds: Set<string>;
}

/** Split locks into the resource kinds a destination picker cares about. */
export function lockSets(locks: FacilityLock[]): LockSets {
  const sets: LockSets = {
    dockIds: new Set(),
    yardSpotIds: new Set(),
    trailerIds: new Set()
  };

  locks.forEach((lock) => {
    const typename = lock.resource?.__typename;
    const uuid = lock.resource_uuid || lock.resource?.uuid;
    if (!uuid) return;

    if (
      lock.model_name === 'DockObject' ||
      lock.model_name === 'Dock' ||
      typename === 'DockObject'
    ) {
      sets.dockIds.add(uuid);
    } else if (
      lock.model_name === 'YardSpot' ||
      lock.model_name === 'YardSpotObject' ||
      typename === 'YardSpotObject'
    ) {
      sets.yardSpotIds.add(uuid);
    } else if (lock.model_name === 'Trailer' || typename === 'Trailer') {
      sets.trailerIds.add(uuid);
    }
  });

  return sets;
}

/** Locked door, or one holding a trailer nobody is moving, is unavailable; a door whose trailer is moving out is `hasMoveFromTask`. */
export function dockAvailability(
  dock: { dockUuid: string; trailer_uuid?: string | null },
  locks: LockSets
): { isLocked: boolean; hasMoveFromTask: boolean } {
  if (locks.dockIds.has(dock.dockUuid)) return { isLocked: true, hasMoveFromTask: false };
  if (dock.trailer_uuid) {
    return locks.trailerIds.has(dock.trailer_uuid)
      ? { isLocked: false, hasMoveFromTask: true }
      : { isLocked: true, hasMoveFromTask: false };
  }
  return { isLocked: false, hasMoveFromTask: false };
}

/** By non-zero `configuration.order`, else by name with numeric awareness ("2" before "10"). */
export function compareByOrderThenName(
  a: { configuration?: any },
  b: { configuration?: any },
  nameA: string,
  nameB: string
): number {
  const orderA = a.configuration?.order;
  const orderB = b.configuration?.order;
  const hasA = orderA !== null && orderA !== undefined && orderA !== 0;
  const hasB = orderB !== null && orderB !== undefined && orderB !== 0;

  if (hasA && hasB) return orderA - orderB;
  if (hasA) return -1;
  if (hasB) return 1;

  const numA = parseInt(nameA, 10);
  const numB = parseInt(nameB, 10);
  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;

  return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
}

/** Flatten per-facility destinations, anchor first; docks only where the user may dock. */
export function buildCampusDestinations(
  campusFacilities: CampusDestinationFacility[],
  locks: FacilityLock[]
): CampusDestinations {
  const sets = lockSets(locks);
  const ordered = [...campusFacilities].sort(
    (a, b) => Number(b.is_anchor) - Number(a.is_anchor)
  );

  const facilities: DestinationFacility[] = ordered.map((facility) => ({
    facilityId: String(facility.facility_id),
    facilityName: facility.facility_name,
    facilityAlias: facility.facility_alias,
    configuration: facility.facility_configuration,
    isAnchor: facility.is_anchor,
    canDock: facility.can_dock
  }));

  const dockDoors: DestinationDockDoor[] = [];
  const yardSpots: DestinationYardSpot[] = [];
  const facilityZones: DestinationFacilityZone[] = [];

  ordered.forEach((facility) => {
    const facilityId = String(facility.facility_id);
    const facilityName = facility.facility_name;

    if (facility.can_dock) {
      const zoneNames = new Map(
        (facility.warehouse_zones || []).map((zone) => [zone.uuid, zone.name])
      );
      const facilityDocks: DestinationDockDoor[] = [];
      (facility.docks || []).forEach((dock) => {
        if (isDockUnavailableForMovement(dock.configuration)) return;
        facilityDocks.push({
          dockUuid: dock.uuid,
          dockName: dock.name,
          zoneUuid: dock.warehouse_zone_uuid,
          zoneName: zoneNames.get(dock.warehouse_zone_uuid) || '',
          trailer_uuid: dock.trailer_uuid,
          configuration: dock.configuration,
          facilityId,
          facilityName,
          ...dockAvailability(
            { dockUuid: dock.uuid, trailer_uuid: dock.trailer_uuid },
            sets
          )
        });
      });
      facilityDocks.sort((a, b) =>
        compareByOrderThenName(a, b, a.dockName || '', b.dockName || '')
      );
      dockDoors.push(...facilityDocks);
    }

    const zoneNames = new Map<string, string>();
    (facility.facility_zones || []).forEach((zone) => {
      zoneNames.set(zone.uuid, zone.name);
      facilityZones.push({
        facility_zone_uuid: zone.uuid,
        facility_zone_name: zone.name,
        zone_type: zone.zone_type,
        configuration: zone.configuration,
        trailers: [],
        facilityId,
        facilityName
      });
    });

    const facilitySpots: DestinationYardSpot[] = (facility.yard_spots || []).map(
      (spot) => ({
        yard_spot_uuid: spot.uuid,
        yard_spot_name: spot.name,
        facility_zone_uuid: spot.facility_zone_uuid,
        zoneName: zoneNames.get(spot.facility_zone_uuid) || '',
        trailer_uuid: spot.trailer_uuid,
        isOccupied: !!spot.trailer_uuid,
        isLocked: sets.yardSpotIds.has(spot.uuid),
        configuration: spot.configuration,
        facilityId,
        facilityName
      })
    );
    facilitySpots.sort((a, b) =>
      compareByOrderThenName(a, b, a.yard_spot_name || '', b.yard_spot_name || '')
    );
    yardSpots.push(...facilitySpots);
  });

  return { facilities, dockDoors, yardSpots, facilityZones };
}

export type SpotStatus = 'unavailable' | 'in-use' | 'available';

export function spotStatus(spot: {
  configuration?: any;
  isOccupied: boolean;
}): SpotStatus {
  if (spot.configuration?.status === 'unavailable') return 'unavailable';
  return spot.isOccupied ? 'in-use' : 'available';
}

export type DestinationKind = 'yard_spot' | 'facility_zone' | 'dock';

/** The facility a chosen destination belongs to, if it is in `destinations`. */
export function destinationFacilityId(
  destinations: Pick<CampusDestinations, 'dockDoors' | 'yardSpots' | 'facilityZones'>,
  kind: DestinationKind,
  uuid: string | null | undefined
): string | undefined {
  if (!uuid) return undefined;
  if (kind === 'yard_spot') {
    return destinations.yardSpots.find((s) => s.yard_spot_uuid === uuid)?.facilityId;
  }
  if (kind === 'facility_zone') {
    return destinations.facilityZones.find((z) => z.facility_zone_uuid === uuid)
      ?.facilityId;
  }
  return destinations.dockDoors.find((d) => d.dockUuid === uuid)?.facilityId;
}
